import React, { useEffect, useMemo, useState } from "react";
import { Autocomplete, Chip, TextField } from "@mui/material";
import { useTranslation } from "react-i18next";
import { listMeetingClientContacts } from "../../api/Meetings/meetingSessionsApi";
import { normalizeContactOptions } from "../../utils/meetingRecorder";
import { meetingAutocompleteSx } from "./meetingUiStyles";

export default function MeetingClientContactSelect({
  brandName = "",
  value = [],
  onChange,
  disabled = false,
  margin = "none",
}) {
  const { t } = useTranslation();
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const ids = Array.isArray(value)
    ? value.map(Number).filter((n) => n > 0)
    : [];

  useEffect(() => {
    let cancelled = false;
    const marca = String(brandName || "").trim();
    if (!marca) {
      setOptions([]);
      setLoading(false);
      return undefined;
    }
    setLoading(true);
    listMeetingClientContacts(marca)
      .then((rows) => {
        if (!cancelled) setOptions(normalizeContactOptions(rows));
      })
      .catch(() => {
        if (!cancelled) setOptions([]);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [brandName]);

  const selected = useMemo(
    () => options.filter((o) => ids.includes(o.id)),
    [options, ids],
  );

  return (
    <Autocomplete
      sx={meetingAutocompleteSx}
      multiple
      options={options}
      loading={loading}
      disabled={disabled || !String(brandName || "").trim()}
      value={selected}
      onChange={(_, next) => onChange((next || []).map((n) => n.id))}
      getOptionLabel={(option) =>
        option?.customerName
          ? `${option.name} (${option.customerName})`
          : option?.name || ""
      }
      isOptionEqualToValue={(a, b) => Number(a?.id) === Number(b?.id)}
      filterSelectedOptions
      limitTags={2}
      noOptionsText={
        String(brandName || "").trim()
          ? t("Meetings.noClientContacts")
          : t("Meetings.pickBrandFirst")
      }
      renderTags={(tagValue, getTagProps) =>
        tagValue.map((option, index) => (
          <Chip
            {...getTagProps({ index })}
            key={option.id}
            label={option.name}
            size="small"
          />
        ))
      }
      renderInput={(params) => (
        <TextField
          {...params}
          margin={margin}
          label={t("Meetings.clientAttendees")}
          placeholder={t("Meetings.clientAttendeesPlaceholder")}
          fullWidth
        />
      )}
    />
  );
}
